/**
 * RP4 — Outer-stage camera API adapter binding extracted from
 * `Scene3DStage.tsx`.
 *
 * Builds the inner `createStageCameraApi` (pure-math) and wraps it via
 * `createCameraApiAdapter` to satisfy the richer
 * `Scene3DStageProps.StageCameraApi` shape that App.tsx + the command
 * line consume. The adapter is then assigned to `stageCameraApiRef`
 * (the prop forwarded from App.tsx).
 *
 * No behavior changes — pure code move from `Scene3DStage.tsx`.
 */

import { t } from "../../i18n";

import {
  createStageCameraApi,
  type CameraStateSnapshot,
} from "../cameraApi";
import type { PendingCameraTween } from "../cameraApiBinder";
import {
  createCameraApiAdapter,
  type ZoomPickingState,
} from "../cameraApiAdapter";
import type { SpatialIndex } from "../types";
import type { Scene3DStageProps } from "../Scene3DStageProps";
import {
  computeBoundsFromSpatialIndex,
  computeBoundsFromSpatialIndexForIds,
} from "./scenePlaceholder";

interface MountStageCameraApiArgs {
  stageCameraApiRefProp: Scene3DStageProps["stageCameraApiRef"];
  cameraStateRef: { current: CameraStateSnapshot };
  viewportSizeRef: { current: { width: number; height: number } };
  spatialIndexRefForHover: { current: SpatialIndex | null };
  pendingTweenRef: { current: PendingCameraTween | null };
  zoomPickingRef: { current: ZoomPickingState | null };
  selectedIdsRef: { current: readonly string[] };
  onZoomPromptChange: Scene3DStageProps["onZoomPromptChange"];
  /** #284-6 — view-op telemetry hook for completed interactive zoom picks. */
  onZoomViewOpComplete: Scene3DStageProps["onZoomViewOpComplete"];
}

/**
 * Build + assign the camera API adapter to the prop ref. Returns a
 * teardown function the caller's effect should hand back so the prop
 * ref is cleared on unmount.
 */
export function mountStageCameraApi(
  args: MountStageCameraApiArgs,
): () => void {
  const { stageCameraApiRefProp } = args;
  if (!stageCameraApiRefProp) return () => undefined;
  const inner = createStageCameraApi({
    cameraStateRef: args.cameraStateRef,
    viewportSizeRef: args.viewportSizeRef,
    getAllEntitiesBounds: () =>
      computeBoundsFromSpatialIndex(args.spatialIndexRefForHover.current),
    getEntitiesBounds: (ids) =>
      computeBoundsFromSpatialIndexForIds(
        args.spatialIndexRefForHover.current,
        ids,
      ),
    setCameraTween: (target, durationMs) => {
      args.pendingTweenRef.current = {
        target,
        origin: {
          centerX: args.cameraStateRef.current.centerX,
          centerY: args.cameraStateRef.current.centerY,
          zoom: args.cameraStateRef.current.zoom,
        },
        startedAt:
          typeof performance !== "undefined" && performance.now
            ? performance.now()
            : Date.now(),
        durationMs,
      };
    },
    // cmd-pan P2-2 — expose the in-flight tween's end-state so `pan()` chains
    // back-to-back scripted displacements onto the pending target (else the
    // second pan reads the stale pre-pan centre from `cameraStateRef`, which the
    // tween only advances on later frames, and overwrites the first).
    getPendingTweenTarget: () => {
      const pending = args.pendingTweenRef.current;
      return pending ? { ...pending.target } : null;
    },
  });
  const adapter = createCameraApiAdapter({
    inner,
    getSelectedIds: () => args.selectedIdsRef.current,
    setZoomPrompt: (prompt) => {
      if (args.onZoomPromptChange) args.onZoomPromptChange(prompt);
    },
    pickingRef: args.zoomPickingRef,
    // cmd-pan P2-3 — push the EXPLICIT pre-change snapshot when one is supplied
    // (the adapter's `pan()` captures the correct pre-pan base before scheduling
    // the tween, so two rapid scripted pans push two DISTINCT entries); fall back
    // to the live camera for the zoom ops, which snapshot synchronously before
    // they commit.
    snapshotForHistory: (state) => {
      inner.pushHistory(state ?? inner.getCamera());
    },
    // cmd-pan P2-3 — expose the in-flight tween's end-state so `pan()` snapshots
    // the correct pre-pan base for rapid back-to-back pans (the same binding the
    // inner `getPendingTweenTarget` reads to accumulate the displacement).
    getPendingTweenTarget: () => {
      const pending = args.pendingTweenRef.current;
      return pending ? { ...pending.target } : null;
    },
    // cmd-pan r5 P2-3 — back `clearPendingTween` with the live tween ref: nulling
    // it stops `CameraApiBinder` from re-applying the stale tween target the next
    // frame, handing the camera to the realtime LMB-pan drag that started
    // mid-tween (else the tween snaps the view back over the drag).
    clearPendingTween: () => {
      args.pendingTweenRef.current = null;
    },
    prompts: {
      zoomWindowFirstCorner: t("zoom.window_pick"),
      zoomWindowSecondCorner: t("zoom.window_pick_second"),
      zoomObjectSelectObjects: t("zoom.object_pick"),
    },
    onInteractiveZoomComplete: (mode) => {
      args.onZoomViewOpComplete?.(mode);
    },
  });
  stageCameraApiRefProp.current = adapter;
  return () => {
    if (stageCameraApiRefProp.current === adapter) {
      stageCameraApiRefProp.current = null;
    }
  };
}
